"use client"

import { useEffect } from "react"
import { getCapacitorPlatform, isCapacitorNative } from "@/lib/capacitor"
import {
  attachNativeKeyboardViewportListener,
  forceNativeKeyboardClosed,
  forceNativeKeyboardOpen,
  isNativeKeyboardOpen,
} from "@/lib/capacitor/keyboard-open-detect"
import {
  clearPluginKeyboardHeight,
  isModalOverlayOpen,
  resetKeyboardChrome,
  setKeyboardChromeExpanded,
  setPluginKeyboardHeight,
  syncAllKeyboardLayouts,
} from "@/lib/capacitor/keyboard-chrome"
import {
  cancelCapacitorKeyboardScroll,
  scheduleKeyboardScrollForFocusedField,
  shouldAdjustCapacitorKeyboardForFocusedInput,
} from "@/lib/capacitor/scroll-field-into-view"
import { attachEnterKeyListener } from "@/lib/capacitor/mobile-enter-key"

type KeyboardListenerInfo = { keyboardHeight?: number }

function applyPluginKeyboardHeight(info: KeyboardListenerInfo) {
  if (typeof info.keyboardHeight === "number" && info.keyboardHeight > 0) {
    setPluginKeyboardHeight(info.keyboardHeight)
  }
}

/**
 * Capacitor keyboard overlay mode.
 * Android: viewport shrink → layout-shrink chat mode; overlay fallback uses visualViewport.
 * iOS: overlay mode — visualViewport first, plugin keyboardHeight as Simulator fallback.
 */
export function KeyboardViewportManager() {
  useEffect(() => attachEnterKeyListener(), [])

  useEffect(() => {
    if (!isCapacitorNative()) return undefined

    const platform = getCapacitorPlatform()
    const html = document.documentElement
    html.setAttribute("data-capacitor-native", "true")
    if (platform === "ios" || platform === "android") {
      html.setAttribute("data-capacitor-platform", platform)
    }

    let lastFocusedField: HTMLElement | null = null
    let focusOutDebounce = 0

    const onFocusInCapture = (e: FocusEvent) => {
      if (!shouldAdjustCapacitorKeyboardForFocusedInput(e.target)) return
      lastFocusedField = e.target as HTMLElement

      if (
        !isModalOverlayOpen() &&
        e.target instanceof HTMLElement &&
        e.target.closest("main.chat-mobile-fixed")
      ) {
        setKeyboardChromeExpanded(true)
      }

      if (e.target instanceof HTMLElement) {
        syncAllKeyboardLayouts()
        scheduleKeyboardScrollForFocusedField(e.target)
      }
    }

    const onFocusOutCapture = (e: FocusEvent) => {
      if (e.target === lastFocusedField) {
        lastFocusedField = null
      }

      window.clearTimeout(focusOutDebounce)
      focusOutDebounce = window.setTimeout(() => {
        if (shouldAdjustCapacitorKeyboardForFocusedInput(document.activeElement)) return
        if (isNativeKeyboardOpen()) return
        resetKeyboardChrome()
      }, 150)
    }

    const focusedField = () =>
      lastFocusedField ??
      (shouldAdjustCapacitorKeyboardForFocusedInput(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null)

    const onKeyboardShow = () => {
      if (!isModalOverlayOpen()) {
        setKeyboardChromeExpanded(true)
      } else {
        syncAllKeyboardLayouts()
      }

      const target = focusedField()
      if (target) scheduleKeyboardScrollForFocusedField(target)
    }

    const onKeyboardHide = () => {
      syncAllKeyboardLayouts()
      cancelCapacitorKeyboardScroll()
      if (!isModalOverlayOpen()) {
        resetKeyboardChrome()
      }
    }

    const onVisualViewportChange = () => {
      if (!isNativeKeyboardOpen()) return
      syncAllKeyboardLayouts()
      const target = focusedField()
      if (target) scheduleKeyboardScrollForFocusedField(target)
    }

    const detachViewport = attachNativeKeyboardViewportListener({
      onShow: onKeyboardShow,
      onHide: onKeyboardHide,
    })

    document.addEventListener("focusin", onFocusInCapture, true)
    document.addEventListener("focusout", onFocusOutCapture, true)
    window.visualViewport?.addEventListener("resize", onVisualViewportChange)
    window.visualViewport?.addEventListener("scroll", onVisualViewportChange)

    let cancelled = false
    const listenerHandles: Array<{ remove: () => Promise<void> }> = []

    void import("@capacitor/keyboard").then(async ({ Keyboard, KeyboardResize }) => {
      if (cancelled) return

      await Keyboard.setResizeMode({ mode: KeyboardResize.None })

      const onPluginShow = (info: KeyboardListenerInfo) => {
        applyPluginKeyboardHeight(info)
        forceNativeKeyboardOpen()
        onKeyboardShow()
      }

      const onPluginHide = () => {
        clearPluginKeyboardHeight()
        forceNativeKeyboardClosed()
        onKeyboardHide()
      }

      if (platform === "ios") {
        await Keyboard.setScroll({ isDisabled: true })
        listenerHandles.push(
          await Keyboard.addListener("keyboardWillShow", onPluginShow),
          await Keyboard.addListener("keyboardDidShow", onPluginShow),
          await Keyboard.addListener("keyboardWillHide", onPluginHide),
          await Keyboard.addListener("keyboardDidHide", onPluginHide),
        )
        return
      }

      listenerHandles.push(
        await Keyboard.addListener("keyboardDidShow", onPluginShow),
        await Keyboard.addListener("keyboardDidHide", onPluginHide),
      )
    })

    return () => {
      cancelled = true
      detachViewport()
      cancelCapacitorKeyboardScroll()
      window.clearTimeout(focusOutDebounce)
      document.removeEventListener("focusin", onFocusInCapture, true)
      document.removeEventListener("focusout", onFocusOutCapture, true)
      window.visualViewport?.removeEventListener("resize", onVisualViewportChange)
      window.visualViewport?.removeEventListener("scroll", onVisualViewportChange)
      void Promise.all(listenerHandles.map((h) => h.remove()))
      resetKeyboardChrome()
      html.removeAttribute("data-capacitor-native")
      html.removeAttribute("data-capacitor-platform")
    }
  }, [])

  return null
}
